import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import ProductService from '@/services/isomorphic/ProductService';
import { ProductAvailabilityState } from '@/services/models/Inventory';
import {
  IProduct,
  ProductModel,
  type IProductVariationMap
} from '@/services/models/Product';
import {
  VariationAttributeType,
  type VariationAttributeID
} from '@/services/models/Product/variation-attributes';
import type { DTO, Nullable } from '@/type-utils';
import { InvalidArgumentError } from '@/utils/errors';
import { isNullOrEmpty } from '@/utils/null-utils';
import { removeUndefined } from '@/utils/object-utils';
import type ISizeVariationAttribute from '@/services/models/Product/variation-attributes/ISizeVariationAttribute';
import DisplayVariationAttributeType from '../DisplayVariationAttributeType';
import type {
  DisplayVariationAttributeFromType,
  IDisplayColorVariationAttribute,
  IDisplaySizeVariationAttribute
} from '../IDisplayVariationAttribute';
import type IProductDisplay from '../IProductDisplay';

/**
 * Defines the display sort priority for each size gender group.
 *
 * Children's shoe sizing starts with Infant/Toddler (C/T) sizes 0-13,
 * then restarts with Little Kids/Youth (K/Y) sizes 1-7, and finally
 * transitions to Adult sizes. This priority map ensures toddler sizes
 * (gender "kids") are displayed before youth sizes.
 */
const SIZE_GENDER_PRIORITY: Record<ISizeVariationAttribute['gender'], number> =
  { kids: 0, youth: 1, men: 2, women: 2, allgender: 2, unisex: 2 };

interface IProductAdapterProps {
  /**
   * The product to adapt.
   */
  readonly product: IProduct | DTO<IProduct>;
}

/**
 * An adapter for {@link IProduct} to implement the {@link IProductDisplay} interface.
 */
class ProductAdapter implements IProductDisplay {
  private readonly product: ProductModel;

  /**
   * Creates a new product adapter.
   * @param product - The product to adapt.
   */
  private constructor({ product }: IProductAdapterProps) {
    this.product = ProductModel.from(product);
  }

  /** @inheritdoc */
  public getVariationsByType<T extends DisplayVariationAttributeType>(
    type: T
  ): ReadonlyArray<DisplayVariationAttributeFromType<T>> {
    const selectedAttributes = this.selectedVariations;

    switch (type) {
      case DisplayVariationAttributeType.Color: {
        const selectableColors = this.product
          .getSelectableAttributesByType(VariationAttributeType.Color)
          .map((attr) => attr.value);

        return this.product
          .getAvailableAttributesByType(VariationAttributeType.Color)
          .map((attr) => {
            return {
              type: DisplayVariationAttributeType.Color,
              value: attr.value,
              isSelectable: selectableColors.includes(attr.value),
              isSelected:
                attr.value ===
                selectedAttributes[DisplayVariationAttributeType.Color],
              metadata: attr
            } as IDisplayColorVariationAttribute as DisplayVariationAttributeFromType<T>;
          });
      }
      case DisplayVariationAttributeType.Size: {
        const selectableSizes = this.product
          .getSelectableAttributesByType(VariationAttributeType.Size)
          .map((attr) => attr.value);

        return (
          this.product
            .getAvailableAttributesByType(VariationAttributeType.Size)
            .map((attr) => {
              return {
                type: DisplayVariationAttributeType.Size,
                value: attr.value,
                isSelectable: selectableSizes.includes(attr.value),
                isSelected:
                  attr.value ===
                  selectedAttributes[DisplayVariationAttributeType.Size],
                metadata: attr
              } as IDisplaySizeVariationAttribute;
            })
            // ensure that the sizes are sorted numerically,
            // with kids (toddler) sizes before youth sizes.
            .sort((sizeA, sizeB) => {
              const positionA = sizeA.metadata.position;
              const positionB = sizeB.metadata.position;
              const genderA = sizeA.metadata.gender;
              const genderB = sizeB.metadata.gender;

              // Sort by gender group priority first so that kids/toddler
              // sizes (e.g. 11, 12, 13) display before youth sizes (e.g. 1, 2, 3).
              if (genderA !== genderB) {
                return (
                  SIZE_GENDER_PRIORITY[genderA] - SIZE_GENDER_PRIORITY[genderB]
                );
              }

              // Within the same gender group, sort by numeric position.
              // Sort `undefined` positions to the end.
              return positionA !== undefined
                ? positionB !== undefined
                  ? positionA - positionB
                  : -1
                : 1;
            }) as Array<DisplayVariationAttributeFromType<T>>
        );
      }
      default:
        throw new InvalidArgumentError(
          `The variation type "${type}" is not part of this product display.`
        );
    }
  }

  /** @inheritdoc */
  public get selectedVariations(): {
    [DisplayVariationAttributeType.Color]?: string | undefined;
    [DisplayVariationAttributeType.Size]?: string | undefined;
  } {
    // Note: `removeUndefined` is necessary here because `selectedVariations` are
    // frequently used with spread syntax, and `undefined` values will overwrite
    // existing values in the spread, unless the key is removed.
    return removeUndefined({
      [DisplayVariationAttributeType.Color]:
        this.product.getSelectedAttributeByType(VariationAttributeType.Color)
          ?.value,
      [DisplayVariationAttributeType.Size]:
        this.product.getSelectedAttributeByType(VariationAttributeType.Size)
          ?.value
    });
  }

  /** @inheritdoc */
  public get availability(): ProductAvailabilityState {
    return this.product.availabilityState;
  }

  /** @inheritdoc */
  public get isPurchasable(): boolean {
    const { availability } = this;

    const inventoryConfig = ConfigurationService.getConfig('inventory');
    const allowBackorder = inventoryConfig.getSetting('allowBackorder').value;
    const allowPreorder = inventoryConfig.getSetting('allowPreorder').value;

    const stockAvailable =
      availability === ProductAvailabilityState.InStock ||
      (allowBackorder && availability === ProductAvailabilityState.Backorder) ||
      (allowPreorder && availability === ProductAvailabilityState.Preorder);

    return (
      stockAvailable &&
      this.product.isCompleteVariant &&
      !isNullOrEmpty(this.product.upc)
    );
  }

  /** @inheritdoc */
  public get isAccessory(): boolean {
    return this.product.isAccessory;
  }

  /** @inheritdoc */
  public get allVariationTypes(): ReadonlyArray<DisplayVariationAttributeType> {
    const result = [] as Array<DisplayVariationAttributeType>;

    const { availableAttributeTypes } = this.product;

    if (availableAttributeTypes.includes(VariationAttributeType.Color)) {
      result.push(DisplayVariationAttributeType.Color);
    }

    if (availableAttributeTypes.includes(VariationAttributeType.Size)) {
      result.push(DisplayVariationAttributeType.Size);
    }

    return result;
  }

  /** @inheritdoc */
  public get currentProduct(): IProduct {
    return this.product;
  }

  /**
   * Given a set of attribute selections, construct a string that represents
   * the product with those selections in the {@link IProduct.variationMap variation map}.
   *
   * @param attributeSelection - The set of attribute selections that must be
   * met.
   *
   * @returns The key of the product that would meet the provided selections.
   * This key may or may not exist in the product's variation map.
   */
  private getVariationKey(attributeSelection: {
    [key in VariationAttributeType]?: VariationAttributeID;
  }): string {
    const { color, size, width } = attributeSelection;

    const attributeKeys = [];

    if (!isNullOrEmpty(color)) attributeKeys.push(color);
    if (!isNullOrEmpty(size)) attributeKeys.push(size);
    if (!isNullOrEmpty(width)) attributeKeys.push(width);

    return attributeKeys.join(':');
  }

  /** @inheritdoc */
  public getVariationMapEntryOnAttributeSelection(
    variationType: DisplayVariationAttributeType,
    value: string
  ): Nullable<IProductVariationMap[string]> {
    if (!this._isVariationAttributeType(variationType)) {
      throw new InvalidArgumentError(
        `The variation type "${variationType}" is not part of this product display.`
      );
    }

    const updatedSelectedAttributes = {
      ...this.selectedVariations,
      // this is where the "greedy" selection happens, since we prioritize the newer selection.
      [variationType]: value
    };

    const newColor =
      updatedSelectedAttributes[DisplayVariationAttributeType.Color];
    const newSize =
      updatedSelectedAttributes[DisplayVariationAttributeType.Size];

    const updatedSelectionAttributesAsProductVariationAttributes = {
      [VariationAttributeType.Color]: !isNullOrEmpty(newColor)
        ? (`color-${newColor}` as const)
        : undefined,
      [VariationAttributeType.Size]: !isNullOrEmpty(newSize)
        ? (`size-${newSize}` as const)
        : undefined
    };

    return ProductService.getProductVariationMapEntryBySelectedAttributes(
      this.currentProduct,
      updatedSelectionAttributesAsProductVariationAttributes
    );
  }

  /** @inheritdoc */
  public determineResultingOnlineStatusOnAttributeSelection(
    variationType: DisplayVariationAttributeType,
    value: string
  ): boolean {
    return Boolean(
      this.getVariationMapEntryOnAttributeSelection(variationType, value)
        ?.isOnline
    );
  }

  /** @inheritdoc */
  public async unselectVariation(
    variationType: DisplayVariationAttributeType
  ): Promise<ProductAdapter> {
    return this.selectVariation(variationType);
  }

  /** @inheritdoc */
  public async selectVariation(
    variationType: DisplayVariationAttributeType,
    value?: string | undefined
  ): Promise<ProductAdapter> {
    if (!this._isVariationAttributeType(variationType)) {
      throw new InvalidArgumentError(
        `The variation type "${variationType}" is not part of this product display.`
      );
    }

    const updatedSelectedAttributes = {
      ...this.selectedVariations,
      // this is where the "greedy" selection happens, since we prioritize the newer selection.
      [variationType]: value
    };

    const newColor =
      updatedSelectedAttributes[DisplayVariationAttributeType.Color];
    const newSize =
      updatedSelectedAttributes[DisplayVariationAttributeType.Size];

    const updatedSelectionAttributesAsProductVariationAttributes = {
      [VariationAttributeType.Color]: !isNullOrEmpty(newColor)
        ? (`color-${newColor}` as const)
        : undefined,
      [VariationAttributeType.Size]: !isNullOrEmpty(newSize)
        ? (`size-${newSize}` as const)
        : undefined
    };

    const newSKU = ProductService.getProductSKUBySelectedAttributes(
      this.currentProduct,
      updatedSelectionAttributesAsProductVariationAttributes
    );

    if (isNullOrEmpty(newSKU)) {
      throw new InvalidArgumentError(
        `Could not find a variant for the selected attributes: ${JSON.stringify(
          updatedSelectedAttributes
        )}`
      );
    }

    const product = await ProductService.getProduct(newSKU);

    return new ProductAdapter({ product });
  }

  /** @inheritdoc */
  public determineResultingUpcOnAttributeSelection(
    variationType: DisplayVariationAttributeType,
    value: string
  ): Nullable<string> {
    return (
      this.getVariationMapEntryOnAttributeSelection(variationType, value)
        ?.upc ?? null
    );
  }

  /**
   * Creates a new product adapter from a product.
   * @param product - The product to create the adapter from.
   * @returns The created product adapter.
   */
  public static fromProduct(product: IProduct | DTO<IProduct>): ProductAdapter {
    return new ProductAdapter({ product });
  }

  /**
   * Checks if the given value is a valid variation attribute type.
   * @param value - The value to check.
   * @returns `true` if the value is a valid variation attribute type, otherwise `false`.
   */
  private _isVariationAttributeType(
    value: string
  ): value is DisplayVariationAttributeType {
    return (
      value === DisplayVariationAttributeType.Color ||
      value === DisplayVariationAttributeType.Size
    );
  }

  /** @inheritdoc */
  public withCurrentProduct(product: IProduct | DTO<IProduct>): ProductAdapter {
    if (product.styleNumber !== this.product.styleNumber) {
      throw new InvalidArgumentError(
        `Received product "${product.sku}" is not part of this product display.`
      );
    }

    return new ProductAdapter({ product });
  }

  /** @inheritdoc */
  public get variationUPCs(): ReadonlyArray<string> {
    return this.product.variationUPCs;
  }
}

export default ProductAdapter;
